/**
 * All guide copy in one place. `resolveCopy` merges DEFAULT_COPY ← VARIANT_OVERRIDES
 * ← PERSONA_OVERRIDES ← GRAPH_TYPE_OVERRIDES: the persona says what the loop is for,
 * the graph type says what the material is (a Search Intent graph is queries, not
 * pages), so it has the last word on wording.
 */

export const STAGES = {
    CONNECT: 'connect',
    BRING: 'bring',
    TOPICS: 'topics',
    SUMMARIZE: 'summarize',
    GAP_VIEW: 'gapView',
    GAP: 'gap',
    SAVE_TO_NOTES: 'saveToNotes',
    EXTEND: 'extend',
    EXPLORE: 'explore',
}

const ALL_STAGES = Object.values(STAGES)

// The profile's persona codes (backend profile.constants PERSONAS, mirrored in
// PersonaToast/personas.js). `other` and an unanswered question both take the
// default flow.
export const PERSONAS = {
    BUILDER: 'ai_workflow_builder',
    SEO: 'seo_content_strategist',
    PKM: 'pkm_obsidian',
    RESEARCHER: 'independent_researcher',
    OTHER: 'other',
}

const DEFAULT_FLOW = [
    STAGES.BRING,
    STAGES.TOPICS,
    STAGES.SUMMARIZE,
    STAGES.GAP_VIEW,
    STAGES.GAP,
    STAGES.SAVE_TO_NOTES,
    STAGES.EXPLORE,
]

// Only the ends of the loop differ by persona: the builder connects before
// bringing material, the researcher deepens the graph before closing. The
// middle (topics → notes) is the product's one loop and stays shared.
const PERSONA_FLOWS = {
    [PERSONAS.BUILDER]: [STAGES.CONNECT, ...DEFAULT_FLOW],
    [PERSONAS.RESEARCHER]: [
        ...DEFAULT_FLOW.slice(0, -1),
        STAGES.EXTEND,
        STAGES.EXPLORE,
    ],
}

// Callers ask this, never DEFAULT_FLOW.
export function getStageFlow(persona) {
    return PERSONA_FLOWS[persona] || DEFAULT_FLOW
}

/**
 * The visible step each stage belongs to, per persona. Steps 2 and 3 of the
 * default flow span several stages; the trail names the step, so the name must
 * not change under the user mid-step.
 */
const DEFAULT_STEP_NAMES = {
    1: '1. Add material',
    2: '2. Reveal the topics',
    3: '3. Find the gaps',
    4: '4. Connect your tool',
}

const PERSONA_STEP_NAMES = {
    [PERSONAS.BUILDER]: {
        1: '1. Connect your tool',
        2: '2. Build from your agent',
        3: '3. Reveal the topics',
        4: '4. Find the gaps',
        5: '5. Close the loop',
    },
    [PERSONAS.SEO]: {
        1: '1. Add a search query',
        2: '2. Reveal the topics',
        3: '3. Find your angle',
        4: '4. Take it to Google',
    },
    [PERSONAS.PKM]: {
        1: '1. Add your notes',
        2: '2. Reveal the clusters',
        3: '3. Find the missing note',
        4: '4. Keep it alive',
    },
    [PERSONAS.RESEARCHER]: {
        1: '1. Add a document',
        2: '2. Reveal the themes',
        3: '3. Find the question',
        4: '4. Add more',
        5: '5. Share',
    },
}

export function getStepNames(persona) {
    return PERSONA_STEP_NAMES[persona] || DEFAULT_STEP_NAMES
}

const DEFAULT_STAGE_STEPS = {
    [STAGES.BRING]: 1,
    [STAGES.TOPICS]: 2,
    [STAGES.SUMMARIZE]: 2,
    [STAGES.GAP_VIEW]: 3,
    [STAGES.GAP]: 3,
    [STAGES.SAVE_TO_NOTES]: 3,
    [STAGES.EXPLORE]: 4,
}

const PERSONA_STAGE_STEPS = {
    [PERSONAS.BUILDER]: {
        [STAGES.CONNECT]: 1,
        [STAGES.BRING]: 2,
        [STAGES.TOPICS]: 3,
        [STAGES.SUMMARIZE]: 3,
        [STAGES.GAP_VIEW]: 4,
        [STAGES.GAP]: 4,
        [STAGES.SAVE_TO_NOTES]: 4,
        [STAGES.EXPLORE]: 5,
    },
    [PERSONAS.RESEARCHER]: {
        ...DEFAULT_STAGE_STEPS,
        [STAGES.EXTEND]: 4,
        [STAGES.EXPLORE]: 5,
    },
}

/** For reporting at emit sites that don't resolve copy; `stage` stays the funnel key. */
export function getStepForStage(stage, persona) {
    const steps = PERSONA_STAGE_STEPS[persona] || DEFAULT_STAGE_STEPS
    return steps[stage] ?? null
}

/**
 * The control each stage points at; a selector matching nothing simply doesn't draw.
 * Package controls carry testids, the layer chips are matched on their tooltip.
 */
// On phones the analytics panel starts folded, taking the AI button and the
// layer chips with it; the ring then lands on the button that opens it.
const OPEN_ANALYTICS_PANEL = 'button[aria-label="open analytics panel"]'

const BUILD_CARD = '[data-testid="quickstart-build-card"]'

export const STAGE_SPOTLIGHTS = {
    [STAGES.CONNECT]: { selector: '[data-testid="quickstart-connect-column"]' },
    [STAGES.BRING]: { selector: BUILD_CARD },
    // `topics` has no DOM target: the ring walks the topic nodes on the graph.
    [STAGES.SUMMARIZE]: {
        selector: '[data-testid="graph-ai-action"]',
        fallbackSelector: OPEN_ANALYTICS_PANEL,
    },
    [STAGES.GAP_VIEW]: {
        selector: 'button[title="reveal the blind spots between topics"]',
        fallbackSelector: OPEN_ANALYTICS_PANEL,
    },
    [STAGES.GAP]: {
        selector: '[data-testid="gap-bridge-button"]',
        round: true,
    },
    [STAGES.SAVE_TO_NOTES]: { selector: '[data-testid="ai-save-to-notes"]' },
}

// Where the persona's first step points instead; `null` rings nothing (the
// builder's first graph is built outside the app).
const PERSONA_SPOTLIGHTS = {
    [PERSONAS.BUILDER]: { [STAGES.BRING]: null },
    [PERSONAS.PKM]: {
        [STAGES.BRING]: { selector: '[data-testid="quickstart-source-documents"]' },
    },
    [PERSONAS.RESEARCHER]: {
        [STAGES.EXPLORE]: { selector: '[aria-label^="share-graph-header"]' },
    },
}

export function getStageSpotlight(stage, persona) {
    const overrides = PERSONA_SPOTLIGHTS[persona]
    if (overrides && stage in overrides) return overrides[stage]
    return STAGE_SPOTLIGHTS[stage] || null
}

// The closing step's real action on /api-access (ApiTokenManager).
export const API_PAGE_SPOTLIGHT = '[data-testid="api-create-key"]'

/**
 * The "or generate from" sources on /apps while the guide asks for the first
 * import: only those the persona's first step is about, so the choice is short.
 * Keys are QuickStartSection's source keys. Personas missing here fall back to
 * the sources that need no setup (`hiddenDuringGuide` in QuickStartSection).
 */
export const BRING_SOURCE_KEYS = {
    [PERSONAS.BUILDER]: ['aiPrompt', 'copiedText'],
    [PERSONAS.SEO]: ['google', 'searchIntent', 'websites', 'copiedText'],
    [PERSONAS.PKM]: ['documents', 'copiedText'],
    [PERSONAS.RESEARCHER]: ['documents', 'csv', 'copiedText'],
}

/** Progress inside a stage that doesn't complete it — swaps in `actionObserved`. */
export const STAGE_PRECURSOR_SIGNALS = {
    [STAGES.GAP]: 'gapHintOpened',
}

// The builder's bring stage: the graph the agent built has shown up in the
// user's list, so the toast can offer to open it.
export const BUILDER_BRING_PRECURSOR_SIGNAL = 'agentGraphAppeared'

// Stage names persisted by earlier guide versions.
const LEGACY_STAGE_MAP = {
    topicsRead: STAGES.TOPICS,
    gapBridged: STAGES.SAVE_TO_NOTES,
    bridge: STAGES.EXPLORE,
}

export function normalizeStage(stage) {
    if (ALL_STAGES.includes(stage)) return stage
    return LEGACY_STAGE_MAP[stage] || STAGES.BRING
}

/**
 * Per-stage content.
 *   title            — the only headline; every stage has one
 *   lede             — optional lighter sentence under the title
 *   body             — paragraph or list; an entry may be { text, muted: true }
 *   action           — the ask, line 1 of the action block
 *   actionObserved   — replaces `action` once the stage's precursor signal fires
 *   hint             — line 2 of the action block (where the control is)
 *   status           — done-marker prefix on line 1 ("Topic selected")
 *   statusAwaitingAi — replaces the block while the AI answers
 *   statusAnswered   — replaces it once the answer has landed, while the user reads
 *   actionButton     — footer button label when `action` would read as the
 *                      button doing the work; falls back to `action`
 *   actionButtonOpen — footer button label while the setup card is open
 *   actionAlt        — ghost button label
 *   actionIcon       — 'arrow' (default) | 'plug'
 *   aiTone           — purple AI accent instead of the blue action accent
 *   setupCard        — 'mcp' | 'obsidian': the closing step's expandable card and
 *                      the tab it opens on; absent, the closing button completes
 *   actionHref       — the closing button opens this in a new tab and completes
 *   actionTo         — the button navigates here (in-app) instead of acknowledging
 * `step` is not written here: getStepForStage() assigns it per persona.
 */
const DEFAULT_COPY = {
    [STAGES.CONNECT]: {
        title: '1. Connect InfraNodus to your AI agent.',
        body: [
            'InfraNodus gives your agent a knowledge graph it can read, extend and find gaps in. Connect it first.',
            {
                text:
                    'Claude, ChatGPT, Cursor or Obsidian, through MCP. Takes about a minute.',
                muted: true,
            },
        ],
        action: 'Create your API key',
        actionButton: 'Let me create a key',
        actionTo: '/api-access',
        actionIcon: 'plug',
        hint: 'Then add https://mcp.infranodus.com in your tool',
        status: null,
        aiTone: false,
        languagePicker: false,
    },
    [STAGES.BRING]: {
        title: '1. Give InfraNodus something to think with.',
        body: [
            'Paste some text, upload a file, search Google results, or ask AI to generate a knowledge graph.',
            {
                text:
                    'You can also start directly with InfraNodus MCP or the Obsidian plugin.',
                muted: true,
            },
        ],
        action: 'Choose Text to Generate Insights',
        actionButton: 'Let me choose the text',
        hint: null,
        status: null,
        aiTone: false,
        languagePicker: true,
    },
    [STAGES.TOPICS]: {
        title: '2. This is the shape of your material.',
        body:
            'Related ideas form topics, each shown in a different color. Pick a topic on the graph or in the Topics panel to explore it.',
        action: 'Choose a topic to see the text behind it',
        actionButton: 'OK, understood. Let me pick a topic.',
        hint: 'Bottom right: Analytics panel → Topics',
        status: null,
        aiTone: true,
        languagePicker: false,
    },
    [STAGES.SUMMARIZE]: {
        title: '2. This is the shape of your material.',
        body: [
            {
                text:
                    'Every topic consists of connections between several words. You can see that on the graph now.',
                muted: true,
            },
            'Click the AI button in the graph controls to summarize the topic you selected.',
        ],
        action: 'Summarize selected topic with AI',
        actionButton: 'Let me summarize this topic',
        hint: 'Bottom right, above the Analytics panel',
        status: 'Topic selected',
        statusAwaitingAi: 'Summarizing your topic…',
        statusAnswered: 'Summary ready. Take a moment to read it.',
        aiTone: true,
        languagePicker: false,
    },
    [STAGES.GAP_VIEW]: {
        title: '3. Find the gaps',
        lede:
            'You’ve seen what’s already there. Now look at what might be missing.',
        body:
            'Some topics are poorly connected. The gaps between them can reveal questions or ideas that address those blind spots.',
        action: 'Switch to Gaps',
        actionButton: 'Let me switch to Gaps',
        hint: 'Bottom right: Analytics panel → Gaps',
        status: null,
        aiTone: false,
        languagePicker: false,
    },
    [STAGES.GAP]: {
        title: '3. Find the gaps',
        body: [
            {
                text:
                    'Here’s one gap: both topics matter in your text, but they are poorly connected.',
                muted: true,
            },
            'Press the AI button on the dotted connection between the topics to explore what could bridge them.',
        ],
        action: 'Bridge this gap with AI',
        actionObserved: 'Ask the AI to bridge it',
        actionButton: 'Let me bridge this gap with AI',
        hint: 'In the middle of the dotted line between the two topics',
        status: 'Gap selected',
        statusAwaitingAi: 'Bridging the two topics…',
        statusAnswered:
            'The AI’s idea for bridging the gap is ready. Take a moment to read it.',
        aiTone: true,
        languagePicker: false,
    },
    [STAGES.SAVE_TO_NOTES]: {
        title: '4. Save what matters.',
        body:
            'Save the response to your Project Notes, or select just the part you want to remember.',
        action: 'Save this insight',
        actionButton: 'Let me save this insight',
        hint: null,
        status: null,
        aiTone: false,
        languagePicker: false,
    },
    [STAGES.EXTEND]: {
        title: '4. Add the next document.',
        body: [
            'Add a second document, transcript or set of responses to this same graph and watch the themes shift.',
            {
                text:
                    'Open the statements panel and add text, or import another file into this graph.',
                muted: true,
            },
        ],
        action: 'Add more material to this graph',
        actionButton: 'Let me add more',
        hint: 'Bottom left: the statements panel',
        status: null,
        aiTone: false,
        languagePicker: false,
    },
    [STAGES.EXPLORE]: {
        title: 'Your first workflow is complete!',
        body: [
            'You brought your material in, read its DNA, found a gap, developed it with AI, and saved what mattered.',
            {
                text:
                    'This works on any graph, any time. Explore many more features of InfraNodus to find interesting insights and missing connections.',
                muted: true,
            },
        ],
        action: 'Connect InfraNodus to your AI tools',
        actionButton: 'Set it up in a minute',
        actionButtonOpen: 'Hide setup',
        actionAlt: 'Maybe later',
        actionIcon: 'plug',
        setupCard: 'mcp',
        hint:
            'Use it from Claude, ChatGPT, Cursor, or Obsidian through MCP. Takes about a minute.',
        status: null,
        aiTone: false,
        languagePicker: false,
    },
}

/**
 * The closing step as seen on /api-access. `step` is null: the footer names the step
 * in plain text instead of drawing the trail.
 */
export const API_PAGE_COPY = {
    step: null,
    stepName: 'Connect your tool',
    title: 'Connect InfraNodus to your favorite tools.',
    body:
        'An API key lets Claude, ChatGPT, Cursor, or Obsidian read and build knowledge graphs, as well as get access to external data: from Google search results to search queries and YouTube videos.',
    action: 'Create your API key',
    actionButton: 'Let me create a key',
    hint: null,
    status: null,
    aiTone: false,
    languagePicker: false,
}

// Keys mirror DEFAULT_COPY; keywordgraph would need SEO-first framing.
const VARIANT_OVERRIDES = {
    keywordgraph: {},
}

/**
 * One loop, four vocabularies. Only the lines that change are listed; a stage
 * absent here reads as the default. Titles carry the persona's step number.
 */
const PERSONA_OVERRIDES = {
    [PERSONAS.BUILDER]: {
        [STAGES.BRING]: {
            title: '2. Build your first graph from your agent.',
            body: [
                'In Claude, ChatGPT or Cursor, ask: “Build an InfraNodus knowledge graph from our release notes and show me the gaps.”',
                'Then come back to InfraNodus, open the graph and continue here.',
            ],
            action: 'Ask your agent to build a graph',
            actionObserved: 'Your agent built this. Open it to continue.',
            actionButton: 'Got it, I’ll ask my agent',
            hint:
                'No agent handy? Use the AI Prompt source below to generate one.',
            languagePicker: false,
        },
        [STAGES.TOPICS]: {
            title: '3. This is what your agent knows.',
            body:
                'Related ideas form topics, each shown in a different color. Pick a topic on the graph or in the Topics panel to see the text behind it.',
        },
        [STAGES.SUMMARIZE]: {
            title: '3. This is what your agent knows.',
        },
        [STAGES.GAP_VIEW]: {
            title: '4. Find the gaps in what it knows',
            lede: 'You’ve seen what’s there. Now see what’s missing.',
            body:
                'Some topics are poorly connected. The gaps between them are what your agent does not yet know how to relate.',
        },
        [STAGES.GAP]: {
            title: '4. Find the gaps in what it knows',
            body: [
                {
                    text:
                        'Here’s one gap: both topics matter, but they are poorly connected.',
                    muted: true,
                },
                'Press the AI button on the dotted connection to get a bridge: a lesson worth adding to its memory.',
            ],
        },
        [STAGES.SAVE_TO_NOTES]: {
            title: '4. Save it to memory.',
            body:
                'Save the response to Project Notes. That is what your agent reads back through MCP.',
        },
        [STAGES.EXPLORE]: {
            title: 'The loop is closed on this side.',
            body: [
                'Now ask your agent: “What are the content gaps in this graph?” It reads the graph and the notes you just saved.',
                {
                    text:
                        'This works on any graph you or your agent build from now on.',
                    muted: true,
                },
            ],
            action: 'Ask your agent about this graph',
            actionButton: 'Done, I asked',
            actionButtonOpen: null,
            setupCard: null,
            hint:
                'In Claude, ChatGPT or Cursor, through the InfraNodus MCP server.',
        },
    },
    [PERSONAS.SEO]: {
        [STAGES.BRING]: {
            title: '1. Start from a search query.',
            body: [
                'Type a query you want to rank for. Google shows what the top pages cover; Search Intent shows what people search for.',
                {
                    text:
                        'You can also paste a competitor’s page as plain text.',
                    muted: true,
                },
            ],
            action: 'Pick Google or Search Intent below',
            actionButton: 'Let me add a query',
        },
        [STAGES.TOPICS]: {
            title: '2. These are the topics the top results cover.',
            body:
                'Each color is a topic the ranking pages share. Pick one on the graph or in the Topics panel to see the pages behind it.',
        },
        [STAGES.SUMMARIZE]: {
            title: '2. These are the topics the top results cover.',
            body: [
                {
                    text:
                        'Every topic is a cluster of words the pages use together.',
                    muted: true,
                },
                'Click the AI button in the graph controls to summarize what this cluster of pages says.',
            ],
            action: 'Summarize this cluster with AI',
            actionButton: 'Let me summarize this cluster',
            statusAwaitingAi: 'Summarizing the cluster…',
        },
        [STAGES.GAP_VIEW]: {
            title: '3. Find your angle',
            lede: 'You’ve seen what ranks. Now see what nobody connects.',
            body:
                'Some topics rank but are poorly connected. The gap between them is a content angle the top results leave open.',
        },
        [STAGES.GAP]: {
            title: '3. Find your angle',
            body: [
                {
                    text:
                        'Here’s one gap: both topics rank, but no page connects them.',
                    muted: true,
                },
                'Press the AI button on the dotted connection to get the angle that would.',
            ],
            action: 'Get the angle with AI',
            actionObserved: 'Ask the AI for the angle',
            actionButton: 'Let me get the angle with AI',
            statusAwaitingAi: 'Looking for the angle…',
            statusAnswered:
                'Your content angle is ready. Take a moment to read it.',
        },
        [STAGES.SAVE_TO_NOTES]: {
            title: '4. Save it as your content brief.',
            body:
                'Save the response to your Project Notes, or just the part you want in the brief.',
            action: 'Save this to the brief',
            actionButton: 'Let me save this',
        },
        [STAGES.EXPLORE]: {
            title: 'Your first angle is ready!',
            body: [
                'Next, add the other half: if you started from Google results, import the Search Intent queries for the same topic (or the other way round) to compare demand and supply.',
                {
                    text:
                        'The browser extension builds this graph on any Google results page.',
                    muted: true,
                },
            ],
            action: 'Get the browser extension',
            actionButton: 'Show me the extension',
            actionButtonOpen: null,
            actionHref: '/extension',
            setupCard: null,
            hint: 'Later, use it from Claude or ChatGPT through MCP.',
        },
    },
    [PERSONAS.PKM]: {
        [STAGES.BRING]: {
            title: '1. Bring in one folder of notes.',
            body: [
                'Upload the Markdown files of one folder or one project. Not the whole vault: a focused set gives a readable graph.',
                {
                    text:
                        'Have the Obsidian plugin? Open a note there and run Analyze instead; the graph appears here.',
                    muted: true,
                },
            ],
            action: 'Choose PDF, TXT & Markdown below',
            actionButton: 'Let me upload my notes',
        },
        [STAGES.TOPICS]: {
            title: '2. These are the clusters across your notes.',
            body:
                'Your graph view, with structure: ideas that recur together form a cluster, each in its own color. Pick one to see the notes behind it.',
            action: 'Choose a cluster to see the notes behind it',
            actionButton: 'OK, understood. Let me pick a cluster.',
        },
        [STAGES.SUMMARIZE]: {
            title: '2. These are the clusters across your notes.',
            body: [
                {
                    text:
                        'Every cluster is a set of words your notes use together.',
                    muted: true,
                },
                'Click the AI button in the graph controls to summarize the cluster you selected.',
            ],
            action: 'Summarize this cluster with AI',
            actionButton: 'Let me summarize this cluster',
            status: 'Cluster selected',
            statusAwaitingAi: 'Summarizing the cluster…',
        },
        [STAGES.GAP_VIEW]: {
            title: '3. Find the note you haven’t written yet',
            lede:
                'You’ve seen what your notes already connect. Now look at what they don’t.',
            body:
                'Two clusters that matter but rarely meet: the gap between them is a note that does not exist yet.',
        },
        [STAGES.GAP]: {
            title: '3. Find the note you haven’t written yet',
            body: [
                {
                    text:
                        'Here’s one gap: both clusters matter in your notes, but nothing links them.',
                    muted: true,
                },
                'Press the AI button on the dotted connection to get the seed of that note.',
            ],
            action: 'Draft the missing note with AI',
            actionObserved: 'Ask the AI for the draft',
            actionButton: 'Let me draft it with AI',
            statusAwaitingAi: 'Drafting the missing note…',
            statusAnswered:
                'The seed of your new note is ready. Take a moment to read it.',
        },
        [STAGES.SAVE_TO_NOTES]: {
            title: '4. Keep the draft.',
            body:
                'Save the response to your Project Notes: the draft of the note you will write.',
            action: 'Save this draft',
            actionButton: 'Let me save this draft',
        },
        [STAGES.EXPLORE]: {
            title: 'Your first pass is complete!',
            body: [
                'Keep the graph alive: the Obsidian plugin graphs new notes as you write them, and MCP lets Claude answer questions across your vault graph.',
                {
                    text: 'Both together are what makes this stick.',
                    muted: true,
                },
            ],
            action: 'Connect Obsidian and your AI tool',
            setupCard: 'obsidian',
            hint: 'The Obsidian plugin and MCP, about a minute each.',
        },
    },
    [PERSONAS.RESEARCHER]: {
        [STAGES.BRING]: {
            title: '1. Add one document.',
            body: [
                'A PDF, a transcript, a Markdown file, or a CSV of survey answers (one row per respondent). One document first; you will add more in a moment.',
                {
                    text:
                        'Interview transcripts, literature, open-ended responses: anything with text.',
                    muted: true,
                },
            ],
            action: 'Choose PDF, TXT & Markdown, or CSV below',
            actionButton: 'Let me add a document',
        },
        [STAGES.TOPICS]: {
            title: '2. These are the themes in your material.',
            body:
                'Ideas that recur together form a theme, each in its own color. Pick a theme on the graph or in the Topics panel to see the passages behind it.',
            action: 'Choose a theme to see the passages behind it',
            actionButton: 'OK, understood. Let me pick a theme.',
        },
        [STAGES.SUMMARIZE]: {
            title: '2. These are the themes in your material.',
            body: [
                {
                    text:
                        'Every theme is a set of words your material uses together.',
                    muted: true,
                },
                'Click the AI button in the graph controls to summarize what is said within this theme.',
            ],
            action: 'Summarize this theme with AI',
            actionButton: 'Let me summarize this theme',
            status: 'Theme selected',
            statusAwaitingAi: 'Summarizing the theme…',
        },
        [STAGES.GAP_VIEW]: {
            title: '3. Find the question',
            lede: 'You’ve seen the themes. Now look at what is never connected.',
            body:
                'Two themes that both matter but are never discussed together: the gap between them is a research question.',
        },
        [STAGES.GAP]: {
            title: '3. Find the question',
            body: [
                {
                    text:
                        'Here’s one gap: both themes matter, but nothing in the material connects them.',
                    muted: true,
                },
                'Press the AI button on the dotted connection to get the question that would.',
            ],
            action: 'Ask for the research question',
            actionObserved: 'Ask the AI for the question',
            actionButton: 'Let me ask for the question',
            statusAwaitingAi: 'Formulating the question…',
            statusAnswered:
                'Your research question is ready. Take a moment to read it.',
        },
        [STAGES.SAVE_TO_NOTES]: {
            title: '4. Start the findings memo.',
            body:
                'Save the response to your Project Notes, where your findings memo begins.',
            action: 'Save this to the memo',
            actionButton: 'Let me save this',
        },
        [STAGES.EXPLORE]: {
            title: 'Your analysis has a shape!',
            body: [
                'Share or export the graph and your notes, and use the statement filters (by category, by sentiment) to compare groups of respondents.',
                {
                    text:
                        'You can also connect Claude through MCP later to ask questions across the material.',
                    muted: true,
                },
            ],
            action: 'Share or export this graph',
            actionButton: 'Got it, I’m done',
            actionButtonOpen: null,
            actionIcon: 'arrow',
            setupCard: null,
            hint:
                'Top: the share icon next to the graph title. Left: download / export.',
        },
    },
}

// What the material is, from the view the import redirected to: a Search
// Intent graph is queries people type, not pages that rank.
const GRAPH_TYPE_OVERRIDES = {
    keywords: {
        [STAGES.TOPICS]: {
            title: '2. These are the topics people search for.',
            body:
                'Each color is a cluster of search queries that go together. Pick one on the graph or in the Topics panel to see the queries behind it.',
        },
        [STAGES.SUMMARIZE]: {
            title: '2. These are the topics people search for.',
            body: [
                {
                    text:
                        'Every topic is a set of words people combine in their searches.',
                    muted: true,
                },
                'Click the AI button in the graph controls to summarize what people want when they search this.',
            ],
        },
        [STAGES.GAP_VIEW]: {
            body:
                'Some things are searched together but poorly connected. The gap between them is a question no single page answers.',
        },
        [STAGES.GAP]: {
            body: [
                {
                    text:
                        'Here’s one gap: people search for both, but no page answers them together.',
                    muted: true,
                },
                'Press the AI button on the dotted connection to get the angle that would.',
            ],
        },
    },
}

// The graph view tails that say what the material is (import redirects:
// /google for search results, /kwrds for Search Intent queries).
const GRAPH_TYPE_BY_VIEW_TAIL = {
    google: 'google',
    kwrds: 'keywords',
}

export function graphTypeFromPathname(pathname) {
    const segments = (pathname || '').split('/').filter(Boolean)
    return GRAPH_TYPE_BY_VIEW_TAIL[segments[2]] || null
}

export function resolveCopy(stage, { variant, graphType, persona } = {}) {
    return {
        ...DEFAULT_COPY[stage],
        ...(VARIANT_OVERRIDES[variant] || {})[stage],
        ...(PERSONA_OVERRIDES[persona] || {})[stage],
        ...(GRAPH_TYPE_OVERRIDES[graphType] || {})[stage],
        step: getStepForStage(stage, persona),
    }
}

// Duplicated in the static pre-hydration overlay at
// views/components/entries/onboardingBootNarration.ejs — keep in sync.
export const NARRATION_LINES = [
    'reading your material…',
    'mapping concepts…',
    'mapping the main topics…',
    'identifying conceptual gaps…',
    'looking for what’s missing…',
]

export const LANGUAGE_HINT =
    'Auto works well for English, German, French & Russian — otherwise select yours.'

export const MCP_SETUP_INTRO = [
    'Connect InfraNodus to Claude, ChatGPT, Cursor, or Obsidian through MCP.',
    'Map any AI conversation, analyze your material, find gaps, and keep exploring your existing graphs.',
]

// Mirrors infranodus.com/tutorial/connect-mcp-server — keep in sync.
export const MCP_CLIENTS = [
    {
        id: 'claude',
        name: 'Claude',
        steps: [
            'Open Settings → Connectors → Add custom connector',
            'Enter the URL below, name it “InfraNodus”, click Connect',
        ],
        snippet: 'https://mcp.infranodus.com',
    },
    {
        id: 'chatgpt',
        name: 'ChatGPT',
        steps: [
            'Open Settings → Apps → Advanced → enable Developer mode',
            'Add the InfraNodus MCP server and connect your account',
        ],
        snippet: 'https://mcp.infranodus.com',
    },
    {
        id: 'cursor',
        name: 'Cursor',
        steps: [
            'Add the server to ~/.cursor/mcp.json',
            'Reload Cursor — InfraNodus tools appear in the composer',
        ],
        snippet: '{ "infranodus": { "url": "https://mcp.infranodus.com" } }',
    },
    {
        id: 'obsidian',
        name: 'Obsidian',
        steps: [
            'Run the skills installer in your vault folder',
            'Point it at the notes you want graphed',
        ],
        snippet: 'npx skills add infranodus/skills --skill infranodus',
    },
]
